import React, { useState } from 'react';
import { Filter, Building2, Calendar, Check, X, Search } from 'lucide-react';
import { Card, cn } from '../ui';
import { controlClasses } from '../ui/Field';

const getAvailableStatuses = () => [
  { value: 'received', label: 'Novo Pedido' },
  { value: 'preparing', label: 'A Preparar' },
  { value: 'ready-to-pickup', label: 'Pronto p/ Levantamento' },
  { value: 'on-delivery', label: 'Em Entrega' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'refunded', label: 'Reembolsado' },
];

const getAvailableRestaurantes = () => [
  { value: 'Inova Foz', label: 'Inova Foz' },
  { value: 'Inova Trindade', label: 'Inova Trindade' },
];

const chipBase = 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all';
const chipActive = 'bg-brand-soft text-brand border-brand/40 shadow-sm';
const chipIdle = 'bg-surface-2 text-muted border-border hover:text-foreground';

const FiltersSection = ({ filters, onFiltersChange, totalOrders, filteredCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const availableStatuses = getAvailableStatuses();
  const availableRestaurantes = getAvailableRestaurantes();

  const handleStatusToggle = (statusValue) => {
    const currentStatuses = filters.statuses || [];
    const newStatuses = currentStatuses.includes(statusValue)
      ? currentStatuses.filter((s) => s !== statusValue)
      : [...currentStatuses, statusValue];
    onFiltersChange({ ...filters, statuses: newStatuses });
  };

  const handleRestauranteToggle = (restauranteValue) => {
    const currentRestaurantes = filters.restaurantes || [];
    const newRestaurantes = currentRestaurantes.includes(restauranteValue)
      ? currentRestaurantes.filter((r) => r !== restauranteValue)
      : [...currentRestaurantes, restauranteValue];
    onFiltersChange({ ...filters, restaurantes: newRestaurantes });
  };

  const handleDateChange = (value) => {
    onFiltersChange({ ...filters, selectedDate: value, startDate: undefined, endDate: undefined });
  };

  const clearAllFilters = () => {
    onFiltersChange({ statuses: [], restaurantes: [], selectedDate: '', search: '' });
  };

  const hasActiveFilters =
    (filters.statuses && filters.statuses.length > 0) ||
    (filters.restaurantes && filters.restaurantes.length > 0) ||
    filters.selectedDate ||
    (filters.search && filters.search.trim());

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-brand" />
          <h3 className="text-lg font-semibold text-foreground">Filtros</h3>
          {hasActiveFilters && (
            <span className="px-2 py-1 bg-brand-soft text-brand text-xs rounded-full font-semibold">
              {filteredCount} de {totalOrders} pedidos
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Sempre à vista, fora do "Mostrar Filtros": é o filtro que se usa
              com o cliente ao telefone, e não pode estar a um clique de
              distância. Vale para todas as secções. */}
          <div className="relative flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-muted-2" />
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
              placeholder="Pesquisar nº, nome, telefone ou email"
              aria-label="Pesquisar pedidos"
              className={cn(controlClasses, 'py-1.5 text-sm pl-9 w-72')}
            />
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="px-3 py-1 text-xs bg-status-canceled-soft text-status-canceled rounded-lg hover:brightness-95 transition-colors"
            >
              Limpar Filtros
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-3 py-1 text-sm bg-brand-soft text-brand rounded-lg hover:brightness-95 transition-colors"
          >
            {isExpanded ? 'Ocultar' : 'Mostrar'} Filtros
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Estado dos Pedidos (múltipla seleção):</label>
            <div className="flex flex-wrap gap-2">
              {availableStatuses.map((status) => {
                const active = filters.statuses?.includes(status.value);
                return (
                  <button
                    key={status.value}
                    onClick={() => handleStatusToggle(status.value)}
                    className={cn(chipBase, active ? chipActive : chipIdle)}
                  >
                    {status.label}
                    {active && <Check className="h-3 w-3" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Restaurante:
            </label>
            <div className="flex flex-wrap gap-2">
              {availableRestaurantes.map((restaurante) => {
                const active = filters.restaurantes?.includes(restaurante.value);
                return (
                  <button
                    key={restaurante.value}
                    onClick={() => handleRestauranteToggle(restaurante.value)}
                    className={cn(chipBase, active ? chipActive : chipIdle)}
                  >
                    {restaurante.label}
                    {active && <Check className="h-3 w-3" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <Calendar className="inline h-4 w-4 mr-1" />
              Filtrar por Data de Levantamento/Entrega:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filters.selectedDate || ''}
                onChange={(e) => handleDateChange(e.target.value)}
                className={cn(controlClasses, 'text-sm')}
              />
              {filters.selectedDate && (
                <button
                  onClick={() => handleDateChange('')}
                  className="px-2 py-1 text-xs bg-surface-2 text-muted rounded-lg hover:text-foreground transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <p className="text-xs text-muted-2 mt-1">Filtra por horário de levantamento (pickup) ou entrega (delivery)</p>
            {filters.selectedDate && (
              <div className="mt-1">
                <p className="text-xs text-muted-2">
                  Mostrando pedidos para:{' '}
                  {new Date(filters.selectedDate).toLocaleDateString('pt-PT', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-xs text-brand">Filtro ativo: {filters.selectedDate}</p>
              </div>
            )}
          </div>

          {hasActiveFilters && (
            <div className="pt-3 border-t border-border">
              <div className="text-sm text-muted">
                <strong className="text-foreground">Filtros Ativos:</strong>
                <div className="mt-1 space-y-1">
                  {filters.statuses && filters.statuses.length > 0 && (
                    <div>
                      • Estados:{' '}
                      {filters.statuses
                        .map((s) => availableStatuses.find((as) => as.value === s)?.label)
                        .join(', ')}
                    </div>
                  )}
                  {filters.restaurantes && filters.restaurantes.length > 0 && (
                    <div>• Restaurantes: {filters.restaurantes.join(', ')}</div>
                  )}
                  {filters.selectedDate && <div>• Data: {new Date(filters.selectedDate).toLocaleDateString('pt-PT')}</div>}
                  {filters.search && filters.search.trim() && <div>• Pesquisa: "{filters.search.trim()}"</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default FiltersSection;
